import { useRef, useState } from "react";
import { Box, useTheme } from "@mui/material";
import Editor from "@monaco-editor/react";

/**
 * Editor Monaco JSON reutilizável.
 * Usa o tema do MUI (claro/escuro) automaticamente.
 *
 * Props:
 *  - value (string)         : conteúdo JSON
 *  - onChange (fn)          : callback (somente quando readOnly=false)
 *  - height (number|string) : altura fixa (ignorado se autoHeight=true)
 *  - autoHeight (boolean)   : ajusta altura ao conteúdo
 *  - minHeight (number)     : altura mínima quando autoHeight (default 120)
 *  - maxHeight (number)     : altura máxima quando autoHeight (default 600)
 *  - readOnly (boolean)     : modo somente leitura
 *  - bordered (boolean)     : exibe borda externa (default true)
 *  - options (object)       : opções extras do Monaco (mescla com defaults)
 */
export default function JsonMonaco({
    value,
    onChange,
    height = 320,
    autoHeight = false,
    minHeight = 120,
    maxHeight = 600,
    readOnly = false,
    bordered = true,
    options = {},
}) {
    const theme = useTheme();
    const monacoTheme = theme.palette.mode === "dark" ? "vs-dark" : "light";
    const editorRef = useRef(null);
    const [contentHeight, setContentHeight] = useState(minHeight);

    const updateHeight = (editor) => {
        const h = editor.getContentHeight();
        const clamped = Math.min(Math.max(h, minHeight), maxHeight);
        setContentHeight(clamped);
    };

    const handleMount = (editor) => {
        editorRef.current = editor;
        if (autoHeight) {
            updateHeight(editor);
            editor.onDidContentSizeChange(() => updateHeight(editor));
        }
    };

    const finalHeight = autoHeight ? contentHeight : height;

    return (
        <Box
            sx={{
                width: "100%",
                ...(bordered && {
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                }),
            }}
        >
            <Editor
                height={finalHeight}
                defaultLanguage="json"
                language="json"
                theme={monacoTheme}
                value={value ?? ""}
                onChange={readOnly ? undefined : (v) => onChange?.(v ?? "")}
                onMount={handleMount}
                options={{
                    readOnly,
                    minimap: { enabled: false },
                    fontSize: 13,
                    tabSize: 2,
                    automaticLayout: true,
                    scrollBeyondLastLine: false,
                    formatOnPaste: true,
                    formatOnType: true,
                    ...options,
                }}
            />
        </Box>
    );
}
